/* eslint-disable */
import React, { useEffect, useState, useRef } from "react";
import { connect } from "react-redux";
import { getCommunityAggregatedStats } from "../../../actions";
import AppGrids from "../../../components/AppGrids";
import Popup from "reactjs-popup";
import "./styles.scss";
import PeriodSelector from "../../../components/PeriodSelector/PeriodSelector";
import moment from "moment";
import Header from "../../../components/Header/Header";
import { useLocation } from "react-router-dom";
import { history } from "../../../config/stores";

const cardTypes = [
  {
    header: "BOARD MESSAGES",
    firstLine: "TOTAL NUMBER OF BOARD MESSAGES",
    secondLine: "TOTAL NUMBER OF USERS CREATING BOARD MESSAGES",
    firstField: "noticesCount",
    secondField: "usersCreatingNotices",
    tab: "notices",
  },
  {
    header: "CALENDAR",
    firstLine: "TOTAL NUMBER OF CREATED EVENTS",
    secondLine: "TOTAL NUMBER OF USERS WITH CREATED EVENTS",
    firstField: "communityEventsCount",
    secondField: "usersCreatingCommunityEvents",
    tab: "community-calendar",
  },
  {
    header: "VOTING",
    firstLine: "TOTAL NUMBER OF VOTING POLLS",
    secondLine: "TOTAL NUMBER OF USERS CREATING VOTING POLLS",
    firstField: "fundsCount",
    secondField: "usersCreatingFunds",
    tab: "community-meetings",
  },
  {
    header: "CHAT",
    firstLine: "TOTAL NUMBER OF CREATED CHAT ROOMS",
    secondLine: "TOTAL NUMBER OF ACTIVE USERS IN CHAT",
    firstField: "chatsCount",
    secondField: "activeUsersInChat",
    tab: "community-files",
  },
  {
    header: "MEETINGS",
    firstLine: "TOTAL NUMBER OF CREATED MEETINGS",
    secondLine: "TOTAL NUMBER OF USERS PARTICIPATING IN MEETINGS",
    firstField: "meetingsCount",
    secondField: "usersParticipatingInMeetings",
    tab: "community-folders",
  },
  {
    header: "FINANCE - WALLETS",
    firstLine: "TOTAL NUMBER OF CREATED WALLETS",
    secondLine: "TOTAL NUMBER OF USERS USING FINANCES",
    firstField: "communityWalletsCount",
    secondField: "transactionsCount",
    tab: "community-wallets",
  },
  {
    header: "FINANCES - TRANSACTIONS",
    firstLine: "TOTAL NUMBER OF ADDED TRANSACTIONS",
    secondLine: "TOTAL NUMBER OF USERS ADDING TRANSACTIONS",
    firstField: "transactionsCount",
    secondField: "usersAddingTransactions",
    tab: "community-transactions",
  },
  {
    header: "UTILITY BILLS",
    firstLine: "TOTAL NUMBER OF UTILITY BILL PAYMENTS",
    secondLine: "TOTAL NUMBER OF USERS PAYING UTILITY BILLS",
    firstField: "utilityBillPaymentsCount",
    secondField: "usersPayingUtilityBills",
    tab: "community-utility-bills",
  },
  {
    header: "ASSISTANT",
    firstLine: "TOTAL NUMBER OF REQUESTS",
    secondLine: "TOTAL NUMBER OF USERS CREATING REQUESTS",
    firstField: "communityServiceSearchesCount",
    secondField: "usersSearchingServices",
    tab: "community-services",
  },
  {
    header: "FILES",
    firstLine: "UPLOADED FILES",
    secondLine: "TOTAL NUMBER OF USERS UPLOADING FILES",
    firstField: "filesCount",
    secondField: "usersUploadingFiles",
    tab: "community-members",
  },
  {
    header: "ADMINISTRATORS",
    firstLine: "TOTAL NUMBER OF COMMUNITIES WITH ADMINISTRATORS",
    secondLine: "TOTAL NUMBER OF ADMINISTRATORS IN COMMUNITIES",
    firstField: "communitiesWithAdministrators",
    secondField: "administratorsCount",
    tab: "community-print-orders",
  },

  {
    header: "PARTICIPANTS",
    firstLine: "TOTAL NUMBER OF COMMUNITIES",
    secondLine: "TOTAL NUMBER OF USERS IN COMMUNITIES",
    firstField: "participantsCount",
    secondField: "usersInCommunities",
    tab: "community-participants",
  },

  {
    header: "PRINT MATERIALS",
    firstLine: "TOTAL NUMBER OF REQUESTS",
    secondLine: "TOTAL NUMBER OF USERS WHO CREATED REQUESTS",
    firstField: "printOrdersCount",
    secondField: "usersPlacingOrders",
    tab: "community-print-materials",
  },

  {
    header: "FUNDS",
    firstLine: "TOTAL NUMBER OF INITIATED PAYMENTS",
    secondLine: "TOTAL NUMBER OF USERS WHO INITIATED PAYMENTS",
    firstField: "fundsCount",
    secondField: "usersCreatingFunds",
    tab: "community-funds",
  },
];

const AppCommunities = ({ communityStats, getCommunityAggregatedStats }) => {
  const location = useLocation();
  const scrollContainerRef = useRef(null);
  
  // Initialize tab from URL params or default
  const getInitialTab = () => {
    const urlParams = new URLSearchParams(location.search);
    const tabFromUrl = urlParams.get('tab');
    return tabFromUrl && cardTypes.some(card => card.tab === tabFromUrl) ? tabFromUrl : "notices";
  };
  
  const [selTab, setSelTab] = useState(getInitialTab());
  const [selPeriod, setSelPeriod] = useState({
    type: "all",
    startDate: null,
    endDate: null,
    label: "All time"
  });

  // Function to save scroll position
  const saveScrollPosition = () => {
    if (scrollContainerRef.current) {
      const scrollLeft = scrollContainerRef.current.scrollLeft;
      localStorage.setItem('app-communities-scroll', scrollLeft.toString());
    }
  };

  // Function to restore scroll position
  const restoreScrollPosition = () => {
    if (scrollContainerRef.current) {
      const savedScroll = localStorage.getItem('app-communities-scroll');
      if (savedScroll) {
        scrollContainerRef.current.scrollLeft = parseInt(savedScroll, 10);
      }
    }
  };

  // Function to change tab and update URL
  const changeTab = (newTab) => {
    setSelTab(newTab);
    saveScrollPosition();
    
    // Update URL with new tab
    const newUrl = `${location.pathname}?tab=${newTab}`;
    history.push(newUrl);
  };

  // Function to load community stats based on period
  const loadCommunityStats = (period = selPeriod) => {
    let statsPayload = {};
    
    if (period.type === "all" || (!period.startDate && !period.endDate)) {
      statsPayload = { startDate: "", endDate: "" };
    } else if (period.startDate && period.endDate) {
      statsPayload = {
        startDate: moment(period.startDate).format('YYYY-MM-DD HH:mm:ss'),
        endDate: moment(period.endDate).format('YYYY-MM-DD HH:mm:ss'),
      };
    }

    // Load community aggregated stats
    getCommunityAggregatedStats(statsPayload);
  };

  useEffect(() => {
    loadCommunityStats();
    // Restore scroll position after component mounts
    setTimeout(restoreScrollPosition, 100);
  }, []);

  // Update stats when period changes
  useEffect(() => {
    loadCommunityStats(selPeriod);
  }, [selPeriod]);

  // Prepare display stats with community data
  const displayStats = {
    // Card 1 - Notices
    noticesCount: communityStats?.notices?.notices || 0,
    usersCreatingNotices: communityStats?.notices?.users || 0,
    
    // Card 2 - Calendar Events
    communityEventsCount: communityStats?.events?.events || 0,
    usersCreatingCommunityEvents: communityStats?.events?.users || 0,
    
    // Card 3 - Voting (using funds data for polls)
    fundsCount: communityStats?.funds?.funds || 0,
    usersCreatingFunds: communityStats?.funds?.users || 0,
    
    // Card 4 - Chat
    chatsCount: communityStats?.chats?.chats || 0,
    activeUsersInChat: communityStats?.chats?.users || 0,
    
    // Card 5 - Meetings
    meetingsCount: communityStats?.meetings?.meetings || 0,
    usersParticipatingInMeetings: communityStats?.meetings?.participants || 0,
    
    // Card 6 - Wallets
    communityWalletsCount: communityStats?.wallets?.wallets || 0,
    
    // Card 7 - Transactions
    transactionsCount: communityStats?.transactions?.transactions || 0,
    usersAddingTransactions: communityStats?.transactions?.users || 0,
    
    // Card 8 - Utility Bills
    utilityBillPaymentsCount: communityStats?.utilityBills?.payments || 0,
    usersPayingUtilityBills: communityStats?.utilityBills?.users || 0,
    
    // Card 9 - Services
    communityServiceSearchesCount: communityStats?.assistantQueries?.assistantQueries || 0,
    usersSearchingServices: communityStats?.assistantQueries?.users || 0,
    
    // Card 9 - Files
    filesCount: communityStats?.files?.files || 0,
    filesStorageSpace: communityStats?.files?.storageSpace || "0 MB",
    usersUploadingFiles: communityStats?.files?.users || 0,
    
    // Card 10 - Administrators
    communitiesWithAdministrators: communityStats?.administrators?.communities || 0,
    administratorsCount: communityStats?.administrators?.users || 0,
    
    // Card 11 - Participants  
    participantsCount: communityStats?.members?.communities || 0,
    usersInCommunities: communityStats?.members?.users || 0,
    
    // Card 12 - Print Orders
    printOrdersCount: communityStats?.printOrders?.orders || 0,
    usersPlacingOrders: communityStats?.printOrders?.users || 0,
    
    // Additional fields for backward compatibility
    membersCount: communityStats?.members?.users || 0,
    storageSpace: communityStats?.files?.storageSpace || "0 MB",
  };

  return (
    <>
      <Header />
      <div className="main-container app-communities">
        <div
          ref={scrollContainerRef}
          className="app-header-container"
          style={{
            height: "auto",
            width: "100%",
            padding: "10px",
            display: "flex",
            overflowX: "scroll",
            overflowY: "hidden",
          }}
        >
          {cardTypes.map((card, i) => {
            return (
              <div
                key={i}
                className={`card-grid ${
                  card.tab === selTab && "selected"
                }`}
              >
                <div
                  className={`card-header ${
                    card.tab === selTab && "selected"
                  }`}
                  onClick={() => {
                    changeTab(card.tab);
                  }}
                >
                  <h2
                    className={`${
                      card.tab === selTab ? "font-bold" : "font-semiBold"
                    }`}
                    style={{
                      textTransform: "uppercase",
                      width: "70%",
                      marginLeft: "5px",
                      lineHeight: "1.1",
                      fontWeight: "700",
                    }}
                  >
                    {card.header}
                  </h2>
                  <div className="period-info">
                    <p style={{ 
                      fontWeight: "600",
                      whiteSpace: "nowrap" 
                    }}>
                      {selPeriod?.label || "All Time"}
                    </p>
                    <Popup
                      className="popup-header-options-container"
                      trigger={
                        <div
                          className={`settings-icon ${
                            card.tab === selTab && "selected"
                          }`}
                        />
                      }
                      position="bottom right"
                    >
                      {(close) => (
                        <PeriodSelector
                          selectedPeriod={selPeriod.type}
                          isLastCard={i === cardTypes.length - 1}
                          onApply={(period) => {
                            setSelPeriod(period);
                            close();
                          }}
                        />
                      )}
                    </Popup>
                  </div>
                </div>
                <div className="card-body">
                  <div className="card-row">
                    <div className="indicator blue" />
                    <p className="row-data">
                      <b>
                        {displayStats?.[card?.firstField] || "---"}
                      </b>
                      <span className="metric-text">
                        {card.firstLine}
                      </span>
                    </p>
                  </div>
                  <div className="card-row">
                    <div className="indicator blue" />
                    <p className="row-data">
                      <b>{displayStats?.[card?.secondField] || "---"}</b>
                      <span style={{ textTransform: "uppercase", fontSize: "14px" }}>
                        {card.secondLine}
                      </span>
                    </p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
        <div className="body-container" style={{ height: "73%" }}>
          {selTab === "notices" ? (
            <AppGrids.CommunityNotices period={selPeriod} />
          ) : selTab === "community-calendar" ? (
            <AppGrids.CommunityCalendar period={selPeriod} />
          ) : selTab === "community-meetings" ? (
            <AppGrids.Polls period={selPeriod} />
          ) : selTab === "community-files" ? (
            <AppGrids.CommunityChats period={selPeriod} />
          ) : selTab === "community-folders" ? (
            <AppGrids.MeetingsCommunities period={selPeriod} />
          ) : selTab === "community-wallets" ? (
            <AppGrids.CommunityWallets period={selPeriod} />
          ) : selTab === "community-transactions" ? (
            <AppGrids.CommunityTransactions period={selPeriod} />
          ) : selTab === "community-utility-bills" ? (
            <AppGrids.CommunityUtilityBills period={selPeriod} />
          ) : selTab === "community-funds" ? (
            <AppGrids.Payments period={selPeriod} />
          ) : selTab === "community-services" ? (
            <AppGrids.AssistantCommunities period={selPeriod} />
          ) : selTab === "community-members" ? (
            <AppGrids.FilesCommunities period={selPeriod} />
          ) : selTab === "community-participants" ? (
            <AppGrids.CommunityParticipants period={selPeriod} />
          ) : selTab === "community-print-materials" ? (
            <AppGrids.CommunityPrintMaterials period={selPeriod} />
          ) : selTab === "community-print-orders" ? (
            <AppGrids.CommunityAdministrators period={selPeriod} />
          ) : (
            <div>Select a tab to view data</div>
          )}
        </div>
      </div>
    </>
  );
};

const mapStateToProps = (state) => ({
  communityStats: state.appCommunities?.communityStats || {},
});

const mapDispatchToProps = (dispatch) => ({
  getCommunityAggregatedStats: (payload) => dispatch(getCommunityAggregatedStats(payload)),
});

export default connect(mapStateToProps, mapDispatchToProps)(AppCommunities);